"use client";

import React, { useState } from "react";
import { DialogLayout } from "../ui/layouts/DialogLayout";
import { IconButton } from "../ui/components/IconButton";
import { FeatherX } from "@subframe/core";
import { TextField } from "../ui/components/TextField";
import { Button } from "../ui/components/Button";
import { FeatherLogIn } from "@subframe/core";
import { FeatherUserPlus } from "@subframe/core";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { FeatherUser } from "@subframe/core";
import { Alert } from "../ui/components/Alert";
import { FeatherAlertCircle } from "@subframe/core";
import { useAuthContext } from "../contexts/AuthContext";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  open,
  onOpenChange
}) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { signIn, signUp } = useAuthContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    if (isSignUp && password !== confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        const { error } = await signUp(email, password);
        if (error) {
          setError(error.message);
        } else {
          setSuccess("Account created successfully! Please check your email to verify your account.");
          // Reset form
          setEmail("");
          setPassword("");
          setConfirmPassword("");
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          onOpenChange(false);
          // Reset form
          setEmail("");
          setPassword("");
          setConfirmPassword("");
        }
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setError("");
    setSuccess("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setError("");
    setSuccess("");
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-full max-w-md flex-col items-start gap-6 bg-default-background px-8 py-8">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <IconWithBackground size="large" icon={<FeatherUser />} />
            <span className="text-heading-2 font-heading-2 text-default-font">
              {isSignUp ? 'Create Account' : 'Sign In'}
            </span>
          </div>
          <IconButton
            variant="neutral-secondary"
            icon={<FeatherX />}
            onClick={handleClose}
          />
        </div>

        {error && (
          <Alert
            variant="error"
            icon={<FeatherAlertCircle />}
            title="Error"
            description={error}
          />
        )}

        {success && (
          <Alert
            variant="success"
            title="Success"
            description={success}
          />
        )}

        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
          <TextField
            label="Email"
            className="w-full"
          >
            <TextField.Input
              type="email"
              placeholder="Enter your email..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </TextField>

          <TextField
            label="Password"
            className="w-full"
          >
            <TextField.Input
              type="password"
              placeholder="Enter your password..."
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </TextField>

          {isSignUp && (
            <TextField
              label="Confirm Password"
              className="w-full"
            >
              <TextField.Input
                type="password"
                placeholder="Confirm your password..."
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </TextField>
          )}

          <div className="flex w-full flex-col gap-3 pt-4">
            <Button
              type="submit"
              icon={isSignUp ? <FeatherUserPlus /> : <FeatherLogIn />}
              loading={loading}
              disabled={loading}
              className="w-full"
            >
              {isSignUp ? 'Create Account' : 'Sign In'}
            </Button>

            <Button
              type="button"
              variant="neutral-tertiary"
              onClick={toggleMode}
              className="w-full"
            >
              {isSignUp 
                ? 'Already have an account? Sign in' 
                : "Don't have an account? Sign up"
              }
            </Button>
          </div>
        </form>
      </div>
    </DialogLayout>
  );
};